/** @jsx jsx */
import type { FC } from 'react';
import React, { useLayoutEffect } from 'react';
import type { StyledProps } from '@compiled/react';
import { styled, keyframes, css, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import type { DraggableState, DragHandleProps } from '@confluence/tree/entry-points/tree-types.ts';
import { type isDraggingBodyClass } from '@confluence/tree/entry-points/isDraggingBodyCSSClass.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import type { ContentTreeItem } from './data-transformers.ts';
const HIGHLIGHT_FADE = keyframes({
	from: {
		background: token('color.background.brand.bold'),
	},
	to: {
		background: 'none',
	},
});

const notchColorVar = '--notch-color';

const notchStyles = css({
	position: 'absolute',
	insetBlockStart: '50%',
	insetInlineStart: token('space.0'),
	width: '2px',
	height: '12px',
	transform: 'translateY(-50%)',
	backgroundColor: `var(${notchColorVar})`,
});

const getElementBeforeColor = (draggableState: DraggableState | undefined) => {
	if (draggableState === 'dragging') {
		return token('color.icon.disabled');
	}
	return token('color.icon');
};

// Note: this component extracts the props that we don't want to send to the DOM
// This avoids styled errors about these props being passed to the DOM
/* eslint-disable @typescript-eslint/no-unused-vars */
const ElementBeforeBase = ({ isSelected, draggableState, children, ...props }) => {
	return <span {...props}>{children}</span>;
};
/* eslint-enable @typescript-eslint/no-unused-vars */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ElementBefore = styled(ElementBeforeBase)<{
	isSelected: boolean;
	draggableState?: DraggableState;
}>({
	zIndex: 1, // position above the reparent background
	marginRight: token('space.0'),
	marginLeft: token('space.050'),
	display: 'flex',
	height: '24px',
	width: '24px',
	webkitBoxAlign: 'center',
	webkitBoxPack: 'center',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ draggableState }) => getElementBeforeColor(draggableState),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ElementAfter = styled.span({
	display: 'flex',
	flexShrink: 0,
	marginLeft: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkItemContent = styled.div({
	width: '100%',
	display: 'flex',
	minHeight: '24px',
	'-webkit-box-align': 'center',
	alignItems: 'center',
	margin: 0,
	padding: 0,
});

/**
 *  Use before element + z-index to create a pseudo background. There's no plan to indent the hover and active states
 *  on the main item. The indenting here is still important so that reparenting matches the indent of the "merge"
 *  outline + the drop indicator.
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReparentBackground = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':before': {
		position: 'absolute',
		zIndex: 0,
		content: `""`,
		backgroundColor: token('color.background.selected.hovered'),
		height: '100%',
		top: 0,
		left: 0,
		right: 0,
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleSpan = styled.span({
	'-webkit-box-flex': 1,
	flexGrow: 1,
	textAlign: 'left',
	overflow: 'hidden',
	outline: 'none',
	display: 'flex',
	flexDirection: 'column',
	zIndex: 1,
});

type PageTreeLinkProps = {
	item: ContentTreeItem;
	draggableState?: DraggableState;
	isSuperAdmin: boolean;
	indent?: number;
	forceHoverBackground: boolean;
	isSelected: boolean;
	isQuickActionsFocused: boolean;
	href: string | null | undefined;
	isDisabled?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PageTreeLink: React.ComponentType<
	PageTreeLinkProps &
		React.ClassAttributes<HTMLAnchorElement> &
		React.AnchorHTMLAttributes<HTMLAnchorElement> &
		StyledProps & {
			as?: 'a' | 'span';
		}
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.a<PageTreeLinkProps & { as?: 'a' | 'span' }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	() =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- The feature gate conditionally applies the co-located motion styles.
		fg('platform-dst-motion-uplift-list-item') && {
			transition: token('motion.listitem.hovered'),
			'&:hover': {
				transition: token('motion.listitem.hovered'),
			},
			'&:active': {
				transition: token('motion.listitem.pressed'),
			},
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none',
				'&:hover': {
					transition: 'none',
				},
				'&:active': {
					transition: 'none',
				},
			},
		},
	/**
	 * Display
	 */
	{
		position: 'relative', // necessary for absolutely positioned reparent background
		display: 'flex',
		alignItems: 'center',
		cursor: 'pointer',
		/**
		 * Content box
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		width: ({ indent }) => `calc(100% - ${indent}px)`,
		height: '32px',
		minHeight: 24,
		boxSizing: 'border-box',
		WebkitBoxAlign: 'center',
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		border: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		margin: ({ indent }) => `0 0 0 ${indent}px`,
		padding: `0 4px 0  0`,
		'&:active': {
			boxShadow: 'none',
			border: 'none',
		},
		'&:focus': {
			outline: 'none',
			boxShadow: `${token('color.border.selected')} 0px 0px 0px 2px inset`,
		},
		'&:hover': {
			[notchColorVar]: token('color.background.neutral.hovered'),
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	{
		'&::before': {
			content: ' ',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
			width: ({ indent }) => `${indent}px`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
			marginLeft: ({ indent }) => `-${indent}px`,
			height: '24px',
		},
	},
	/**
	 * Background color
	 */
	{
		backgroundColor: 'transparent',
		[notchColorVar]: 'transparent',
		'&:hover, &:focus-within': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active, &:focus': {
			backgroundColor: token('color.background.selected'),
		},
		// force the background color to be transparent while ANY drag is ocurring
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		['body.is-dragging-tree-item &:hover, body.is-dragging-tree-item &:focus-within' satisfies `body.${typeof isDraggingBodyClass} &:hover, body.${typeof isDraggingBodyClass} &:focus-within`]:
			{
				backgroundColor: 'transparent',
			},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isSelected }) =>
		isSelected && {
			backgroundColor: token('color.background.selected'),
			[notchColorVar]: token('color.background.selected.bold'),
			'&:hover, &:focus-within': {
				backgroundColor: token('color.background.selected.hovered'),
				[notchColorVar]: token('color.background.selected.bold'),
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isSelected }) =>
		isSelected &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- The feature gate conditionally applies the co-located motion styles.
		fg('platform-dst-motion-uplift-list-item') && {
			transition: token('motion.listitem.selected'),
			'&:hover': {
				transition: token('motion.listitem.hovered'),
			},
			'&:active': {
				transition: token('motion.listitem.pressed'),
			},
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none',
				'&:hover': {
					transition: 'none',
				},
				'&:active': {
					transition: 'none',
				},
			},
		},
	// when quick actions or hover page cards are open
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ forceHoverBackground }) =>
		forceHoverBackground && {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	/**
	 * Text and color
	 */
	{
		font: token('font.body'),
		userSelect: 'none',
		textDecoration: 'none',
		color: token('color.text.subtle'),
		'&:visited, &:active, &:focus, &:hover': {
			color: token('color.text.subtle'),
			textDecoration: 'none',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isSelected }) =>
		isSelected && {
			'&:visited, &:active, &:focus': {
				color: token('color.text.selected'),
			},
			'&:hover': {
				color: token('color.text.accent.blue'), // TODO: change this to B800 when it becomes available
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isSuperAdmin, item }) =>
		isSuperAdmin &&
		(item.hasRestrictions || item.hasInheritedRestrictions) && {
			color: token('color.text.danger'),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.draggableState === 'dragging' && {
			'& .title': {
				color: token('color.text.disabled'),
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isDisabled && {
			cursor: 'default',
			'&:visited, &:active, &:focus, &:hover': {
				backgroundColor: 'transparent',
			},
			'& .title': {
				color: token('color.text.disabled'),
				opacity: '0.7',
			},
		},
	/**
	 * After icon
	 */
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		'--display-after-icon': ({ forceHoverBackground, isQuickActionsFocused }) =>
			forceHoverBackground || isQuickActionsFocused ? 'flex' : 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		'--opacity-after-icon': ({ forceHoverBackground, isQuickActionsFocused }) =>
			forceHoverBackground || isQuickActionsFocused ? '100%' : '0%',
		// show after icon when hover or focus-within on the link item
		'&:hover, &:focus-within': {
			'--display-after-icon': 'flex',
			'--opacity-after-icon': '100%',
		},
		// force hide after icon when drag is ocurring
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		['body.is-dragging-tree-item &:hover, body.is-dragging-tree-item &:focus-within' satisfies `body.${typeof isDraggingBodyClass} &:hover, body.${typeof isDraggingBodyClass} &:focus-within`]:
			{
				'--display-after-icon': 'none',
				'--opacity-after-icon': '0%',
			},
	},
	/**
	 * Drafts
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ item }) =>
		item.data.status === 'DRAFT' && {
			'& .title': { opacity: '0.7' }, // Drafts should be 70% opacity
		},
	/**
	 * Highlight animation
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ item }) =>
		item.data.isHighlighted && {
			animationName: HIGHLIGHT_FADE,
			animationDuration: '2s',
			animationTimingFunction: 'linear',
		},
	/**
	 * Folder icon styles
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ item, draggableState }) =>
		item.data.type === 'folder' &&
		draggableState === 'dragging' && {
			"[data-component-selector='FolderIconSpan']": {
				backgroundColor: token('color.text.disabled'),
			},
		},
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Active state is applied by data attribute on this element.
		'&[data-is-active="true"]': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
);

type PageTreeLinkItemProps = {
	dragHandleProps?: DragHandleProps;
	onMouseEnter?(event: any): void;
	onMouseLeave?(event: any): void;
	onBlur?(event: any): void;
	onClick(event: any): void;
	beforeIcon: React.ReactNode;
	afterIcon?: React.ReactNode;
	children?: React.ReactNode;
	isActive?: boolean;
	renderAs?: 'a' | 'span';
} & PageTreeLinkProps;

export const focusedAriaCurrentValue = 'page';

const scrollOptions: ScrollIntoViewOptions = {
	behavior: 'auto',
	block: 'center',
	inline: 'nearest',
};

// there should be a valid calculated layout for `scrollIntoView`
// `requestAnimationFrame` allows to execute the scroll right after the layout is calculate
// without it `scrollIntoView` will force the sync layout calculation, which has a negative performance impact
export const ssrInlineScriptContent: string = `requestAnimationFrame(function() {
  const selected = document.querySelector('[aria-current="${focusedAriaCurrentValue}"]');
  if(selected) selected.scrollIntoView(${JSON.stringify(scrollOptions)});
  });
  `;

/**
 * This needs to be placed at the end of side navigation.
 * Because we need to execute it synchronously to allow scroll to be fast.
 * But we can only execute when everything is parsed in the side bar to get the correct scroll position.
 * Also this script is executed as inline script in SSR result as well as in SPA code to avoid jumping.
 *
 * When blog tree is showing and expanded, it will also use this exact
 * script to scroll to the selected blog tree item. We apply the same focusedAriaCurrentValue to the selected blog tree item.
 */
export const FocusToCurrentPageTreeLinkItemSSRInlineScript = (): JSX.Element | null => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	useLayoutEffect(() => {
		if (window.__SSR_RENDERED__) {
			const script = document.createElement('script');
			script.appendChild(document.createTextNode(ssrInlineScriptContent));
			document.body.appendChild(script);
			return () => {
				document.body.removeChild(script);
			};
		}
	}, []);

	if (renderServerPlaceholder) {
		return (
			<script
				/* eslint-disable-next-line react/no-danger */
				dangerouslySetInnerHTML={{
					__html: ssrInlineScriptContent,
				}}
			/>
		);
	}
	return null;
};

export const PageTreeLinkItem: FC<PageTreeLinkItemProps> = ({
	afterIcon,
	beforeIcon,
	children,
	draggableState,
	dragHandleProps,
	indent,
	isActive,
	isSelected,
	renderAs = 'a',
	href,
	isDisabled,
	...passThroughPageTreeLinkProps
}) => {
	return (
		<PageTreeLink
			as={renderAs}
			{...dragHandleProps}
			{...passThroughPageTreeLinkProps}
			// here we must pass in a href prop otherwise the link will be rendered as a button
			// This will break the page as inside the link there is another <button>
			// <button> can't be rendered inside another <button>
			href={renderAs === 'a' && !isDisabled ? href || '#' : undefined}
			aria-current={isSelected ? focusedAriaCurrentValue : undefined}
			data-testid="page-tree-item"
			data-is-active={isActive && !isSelected ? 'true' : undefined}
			draggableState={draggableState}
			indent={indent}
			isSelected={isSelected}
			isDisabled={isDisabled}
			aria-disabled={isDisabled}
		>
			<LinkItemContent>
				{!isDisabled && <div css={notchStyles} aria-hidden="true" />}
				<ElementBefore isSelected={isSelected} draggableState={draggableState}>
					{beforeIcon}
				</ElementBefore>
				<TitleSpan>{children}</TitleSpan>
				<ElementAfter>{afterIcon}</ElementAfter>
			</LinkItemContent>
			{draggableState === 'parent-of-instruction' ? (
				<ReparentBackground data-testid="reparent-background" />
			) : null}
		</PageTreeLink>
	);
};
